export type FontWeightKey = '400' | '500' | '600' | '700'

export type FontRole = 'heading' | 'body'

export const FONT_WEIGHTS: FontWeightKey[] = ['400', '500', '600', '700']

export const DEFAULT_FONT_WEIGHTS = [400, 500, 600, 700]

export interface GoogleFontItem {
  family: string
  category: string
  variants: string[]
  files: Record<string, string>
}

export interface BrandAppFont {
  family: string
  weights: number[]
  files: Record<FontWeightKey, string>
}

export interface WebappFont {
  source: 'google' | 'upload'
  family: string
  files: Partial<Record<FontWeightKey, string>>
}

export type BrandAppFontInput = { source: 'google'; family: string; weights: number[] } | null

export const variantWeight = (variant: string): number | null => {
  if (variant === 'regular') return 400

  return /^[1-9]00$/.test(variant) ? Number(variant) : null
}

export const uprightWeights = (variants: string[]): number[] =>
  [...new Set(variants.map(variantWeight).filter((weight): weight is number => weight !== null))].sort((a, b) => a - b)

export const nearestWeight = (target: number, available: number[]): number =>
  available.reduce((best, weight) => {
    const distance = Math.abs(weight - target)
    const bestDistance = Math.abs(best - target)

    return distance < bestDistance || (distance === bestDistance && weight > best) ? weight : best
  })

const variantKey = (weight: number) => (weight === 400 ? 'regular' : String(weight))

const secureUrl = (url: string) => url.replace(/^http:\/\//, 'https://')

export const toBrandAppFont = (item: GoogleFontItem): BrandAppFont | null => {
  const weights = uprightWeights(item.variants)
  if (weights.length === 0) return null

  const files = Object.fromEntries(
    FONT_WEIGHTS.map((target) => [
      target,
      secureUrl(item.files[variantKey(nearestWeight(Number(target), weights))] ?? '')
    ])
  ) as Record<FontWeightKey, string>
  if (FONT_WEIGHTS.some((target) => !files[target])) return null

  return { family: item.family, weights, files }
}

export const fontFromApi = (font: WebappFont | null | undefined): BrandAppFont | null => {
  if (!font?.family) return null
  const files = Object.fromEntries(FONT_WEIGHTS.map((target) => [target, font.files[target] ?? ''])) as Record<
    FontWeightKey,
    string
  >

  return { family: font.family, weights: [...DEFAULT_FONT_WEIGHTS], files }
}

export const fontInput = (font: BrandAppFont | null | undefined): BrandAppFontInput =>
  font ? { source: 'google', family: font.family, weights: [...font.weights] } : null

export const fontKey = (font: BrandAppFont | null | undefined) => font?.family ?? null

export const previewFont = (font: BrandAppFont | null | undefined) =>
  font && FONT_WEIGHTS.every((weight) => font.files[weight]) ? { family: font.family, files: { ...font.files } } : null

export const fontPreviewUrl = (family: string, text: string) =>
  `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}&text=${encodeURIComponent(text)}&display=swap`

export const matchesFont = (item: GoogleFontItem, search: string, category: string | null) => {
  if (category && item.category !== category) return false
  const query = search.trim().toLocaleLowerCase()

  return !query || item.family.toLocaleLowerCase().includes(query)
}
